import { useMemo } from 'react';
import { BetslipEvent, useBetslipContext } from '../contexts/BetslipContext';
import { useSlipBonusCampaigns } from '../queries';
import { NUM_OF_SELECTIONS_BONUS_ELIGIBILITY, NumOfSelectionsBonusEligibility } from '../types/enums';
import { BetslipTicketType, NumOfSelectionsBonusCampaign } from '../@types';
import { BETSLIP_TICKET_TYPE } from '../constants';
import { isEmpty } from 'lodash-es';

const ticketTypeMap: Record<BetslipTicketType, NumOfSelectionsBonusEligibility> = {
  [BETSLIP_TICKET_TYPE.PRE_MATCH]: NUM_OF_SELECTIONS_BONUS_ELIGIBILITY.PRE_MATCH,
  [BETSLIP_TICKET_TYPE.IN_PLAY]: NUM_OF_SELECTIONS_BONUS_ELIGIBILITY.IN_PLAY,
  [BETSLIP_TICKET_TYPE.MIXED]: NUM_OF_SELECTIONS_BONUS_ELIGIBILITY.MIXED,
};

type BonusEligibilityInfo = {
  nextBonusPercentage: number | null;
  selectionsNeeded: number;
  minRequiredOdd: number;
};

type SelectionsBonusResult = {
  bonusAmount: number;
  bonusPercentage: number;
  hasActiveCampaigns: boolean;
  eligibility: BonusEligibilityInfo | null;
};

const defaultData: SelectionsBonusResult = {
  bonusAmount: 0,
  bonusPercentage: 0,
  hasActiveCampaigns: false,
  eligibility: null,
};

const useSelectionsBonus = (maxPayout: number, skipCalculation?: boolean): SelectionsBonusResult => {
  const { betslipTicketType, bets } = useBetslipContext();
  const { data: bonusCampaigns } = useSlipBonusCampaigns();

  return useMemo(() => {
    if (!bonusCampaigns) {
      return defaultData;
    }

    const ticketType = ticketTypeMap[betslipTicketType];
    const appliedCampaigns = bonusCampaigns[ticketType];

    const hasActiveCampaigns = !isEmpty(appliedCampaigns);

    if (skipCalculation || !hasActiveCampaigns) {
      return { ...defaultData, hasActiveCampaigns };
    }

    let finalBonusPercentage = 0;

    appliedCampaigns.forEach((campaign) => {
      const eligibleBetsCount = getEligibleBetsCount(bets, campaign.minReqOdd || 0);

      // Support for future multiple campaigns for a single ticketType
      finalBonusPercentage += campaign.bonusPercentagePerSelection[eligibleBetsCount] || 0;
    });

    const bonusAmount = maxPayout * (finalBonusPercentage / 100);

    // We will only ever have one campaign per ticket type for now, so getting the first one
    const eligibility = getNextBonusEligibility(
      appliedCampaigns[0],
      getEligibleBetsCount(bets, appliedCampaigns[0].minReqOdd || 0)
    );

    return {
      bonusAmount: bonusAmount || 0,
      bonusPercentage: finalBonusPercentage,
      hasActiveCampaigns,
      eligibility,
    };
  }, [bonusCampaigns, betslipTicketType, skipCalculation, maxPayout, bets]);
};

const getEligibleBetsCount = (bets: BetslipEvent[], minReqOdd: number) =>
  bets.filter((bet) => parseFloat(bet.odds) >= minReqOdd).length;

const getNextBonusEligibility = (
  campaign: NumOfSelectionsBonusCampaign,
  eligibleBetCount: number
): BonusEligibilityInfo | null => {
  const numOfSelectionsForBonus = Object.keys(campaign.bonusPercentagePerSelection)
    .map(Number)
    .sort((a, b) => a - b);

  const nextNumOfSelectionsForBonus = numOfSelectionsForBonus.find(
    (numOfSelections) => numOfSelections > eligibleBetCount
  );

  const minRequiredOdd = campaign.minReqOdd || 1;

  if (!nextNumOfSelectionsForBonus) {
    return {
      nextBonusPercentage: null,
      minRequiredOdd,
      selectionsNeeded: 0,
    };
  }

  const selectionsNeeded = nextNumOfSelectionsForBonus - eligibleBetCount;
  const nextBonusPercentage = campaign.bonusPercentagePerSelection[nextNumOfSelectionsForBonus];

  return {
    nextBonusPercentage,
    selectionsNeeded,
    minRequiredOdd,
  };
};

export default useSelectionsBonus;
